import { CancelRefundFooterProps, CancelRefundHeaderProps, OrderRefundDetails } from '../types'
import { CancelRefundTableData, ICancelRefundTableRow } from '../components/cancelRefundTable/types'
import { RefundFeeType } from 'settings/accountSettings/contract/managementService'
import { BookingFeeRowData, getBookingFeesRowsData } from '../utils'
import { t } from 'translations'

export const mapHeaderProps = (isCancellation: boolean, onClose: () => void): CancelRefundHeaderProps => {
    return {
        title: isCancellation
            ? t('orders.orders.cancelAndRefund.bulk.cancelOrders')
            : t('orders.orders.cancelAndRefund.bulk.refundOrders'),
        subtitle: isCancellation
            ? t('orders.orders.cancelAndRefund.bulk.cancelSubtitle')
            : t('orders.orders.cancelAndRefund.bulk.refundSubtitle'),
        onClose,
    }
}

interface TableDataMapperOptions {
    orders: OrderRefundDetails[]
    selectedOrders: string[]
    totalAmount: number
    partnerRefundFeeAmount: number
    partnerRefundFeeEnabled: boolean
    partnerRefundFeeSelected: boolean
    formatCurrencyString: (amount: number | string, accountSlug: string) => string
    partnerRefundFee?: string
    partnerRefundFeeType?: RefundFeeType | null
    bookingFeesEnabled: boolean
    selectedBookingFees: string[]
    confirmMode: boolean
}

export const prepareTableData = ({
    orders,
    selectedOrders,
    totalAmount,
    partnerRefundFeeAmount,
    partnerRefundFeeEnabled,
    partnerRefundFeeSelected,
    formatCurrencyString,
    partnerRefundFee,
    partnerRefundFeeType,
    bookingFeesEnabled,
    selectedBookingFees,
    confirmMode,
}: TableDataMapperOptions): CancelRefundTableData => {
    const sections = []
    const orderRows: ICancelRefundTableRow[] = []
    const bookingFeeRowsData: BookingFeeRowData[] = []

    for (const order of orders) {
        for (const ticket of order.refundOrderItems) {
            for (const item of ticket.refundTickets) {
                const isSelected = selectedOrders.includes(order.id)

                getBookingFeesRowsData(bookingFeeRowsData, item, isSelected, ticket.product, undefined, ticket.isBundle)
            }
        }
        const orderSelected = selectedOrders.includes(order.id)
        if (confirmMode && !orderSelected) {
            continue
        }

        orderRows.push({
            id: order.id,
            rowType: 'order',
            name: t('orders.orders.cancelAndRefund.bulk.orderName', { number: order.number }),
            description: t('orders.orders.cancelAndRefund.bulk.subtotal', {
                amount: formatCurrencyString(Number(order.totalDiscountedPriceExclTax).toFixed(2), order.account),
            }),
            amount: Number(order.totalDiscountedPriceInclTax),
            selected: orderSelected,
            orderItemId: '',
            disabled: !order.canRefund,
        })
    }

    const ticketsSection = {
        id: 'itemsSection',
        rows: orderRows,
        borderBottom: true,
    }

    sections.push(ticketsSection)

    if (bookingFeesEnabled && bookingFeeRowsData.length > 0) {
        const bookingFeeRows: ICancelRefundTableRow[] = []

        for (const bookingFee of bookingFeeRowsData) {
            const isBookingFeeSelected = selectedBookingFees.includes(bookingFee.id)

            if (!isBookingFeeSelected && confirmMode) {
                continue
            }

            bookingFeeRows.push({
                id: bookingFee.id,
                rowType: 'bookingFee',
                name: bookingFee.name,
                description: t('orders.orders.cancelAndRefund.productsSelected', {
                    selected: bookingFee.amountOfSelectedProducts,
                    total: bookingFee.amountOfProducts,
                }),
                amount: bookingFee.amount,
                selected: isBookingFeeSelected,
                orderItemId: '',
                infoTip: `* ${bookingFee.productName}`,
                disabled: bookingFee.amountOfSelectedProducts === 0,
            })
        }

        const bookingFeeSection = {
            id: 'bookingFeeSection',
            rows: bookingFeeRows,
            borderTop: true,
            borderBottom: true,
        }

        sections.push(bookingFeeSection)
    }

    if (partnerRefundFeeEnabled) {
        const refundFeeSection = {
            id: 'refundFeeSection',
            rows: [
                {
                    id: '',
                    rowType: 'refundFee',
                    name: t('orders.orders.cancelAndRefund.visitorRefundFee'),
                    description:
                        partnerRefundFeeType === 'percentage'
                            ? t('orders.orders.cancelAndRefund.feePercentage', { fee: partnerRefundFee })
                            : t('orders.orders.cancelAndRefund.flatRate'),
                    amount: partnerRefundFeeAmount,
                    selected: partnerRefundFeeSelected,
                    orderItemId: '',
                    infoTip: t('orders.orders.cancelAndRefund.refundFeeInfo'),
                    disabled: true,
                },
            ],
        }

        sections.push(refundFeeSection)
    }

    const totalAmountSection = {
        id: 'totalSection',
        rows: [
            {
                id: 'total',
                rowType: 'total',
                name: t('orders.orders.cancelAndRefund.total'),
                description: '',
                amount: totalAmount,
                selected: false,
                orderItemId: '',
            },
        ],
    }
    sections.push(totalAmountSection)

    return {
        sections,
    }
}

interface FooterPropsMapperOptions {
    accountSlug: string
    confirmMode: boolean
    isCancellation: boolean
    selectedOrders: string[]
    refunding: boolean
    onCancel: () => void
    onNext: () => void
    totalAmount: number
    refundDisallowed: boolean
    formatCurrencyString: (amount: number | string, accountSlug: string) => string
}

export const mapFooterProps = ({
    accountSlug,
    confirmMode,
    isCancellation,
    selectedOrders,
    refunding,
    onCancel,
    onNext,
    totalAmount,
    refundDisallowed,
    formatCurrencyString,
}: FooterPropsMapperOptions): CancelRefundFooterProps => {
    const confirmMessage = (() => {
        if (confirmMode) {
            if (isCancellation) {
                return t('orders.orders.cancelAndRefund.bulk.confirmCancel', { number: selectedOrders.length })
            }
            return t('orders.orders.cancelAndRefund.bulk.confirmRefund', {
                number: selectedOrders.length,
                amount: formatCurrencyString(Number(totalAmount).toFixed(2), accountSlug),
            })
        }

        if (selectedOrders.length >= 20) {
            return t('orders.orders.cancelAndRefund.bulk.maxOrders')
        }
        return
    })()

    const nextButtonCaption = confirmMode
        ? isCancellation
            ? t('orders.orders.cancelAndRefund.bulk.cancelOrders')
            : t('orders.orders.cancelAndRefund.bulk.refundOrders')
        : t('orders.orders.cancelAndRefund.review')

    return {
        confirmMessage,
        cancelButtonCaption: confirmMode ? t('orders.orders.cancelAndRefund.backToEditing') : t('common.cancel'),
        onCancel,
        nextButtonCaption,
        onNext,
        nextButtonDisabled: refundDisallowed || selectedOrders.length === 0 || selectedOrders.length > 20 || refunding,
    }
}
